import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  MustWatchVideoNotification,
  MustWatchVideoTranslations,
} from '@skygroup/shared/components/must-watch-video/constants';
import {
  markVideoNotificationWatchedApi,
  useActiveVideoNotificationsApi,
} from '@skygroup/shared/components/must-watch-video/videoNotificationApi';
import { RootState } from '../store/store';

/**
 * Feeds `VideoNotificationsProvider` from GET /ms/video-notifications/active
 * and confirms videos through POST /ms/video-notifications/:id/watched.
 *
 * Only an agent working in agent mode receives them — regular site visitors
 * never fire the request.
 */
const useMustWatchVideoNotification = () => {
  const { agentInfo, translations } = useSelector(
    (state: RootState) => state.config
  );

  const enabled = !!agentInfo?.agent?.id && !!agentInfo?.agentMode;
  // Namespaces the cache, the remembered ids and the cross-tab channel, so a
  // second agent logging in on the same browser never inherits the first one's
  // queue.
  const scope = agentInfo?.agent?.id ? `agent:${agentInfo.agent.id}` : null;

  const { notifications, markWatched } = useActiveVideoNotificationsApi({
    enabled,
    scope,
  });

  const t = translations?.videoNotification;

  const mustWatchTranslations: MustWatchVideoTranslations = useMemo(
    () => ({
      title: t?.title ?? '',
      badge: t?.badge ?? '',
      optionalLabel: t?.optionalLabel ?? '',
      indicatorTooltip: t?.indicatorTooltip ?? '',
      blockedHint: t?.blockedHint ?? '',
      notificationsTitle: t?.notificationsTitle ?? '',
      newLabel: t?.newLabel ?? '',
      watchedLabel: t?.watchedLabel ?? '',
      seeMore: t?.seeMore ?? '',
      seeLess: t?.seeLess ?? '',
      emptyState: t?.emptyState ?? '',
      prevVideo: t?.prevVideo ?? '',
      nextVideo: t?.nextVideo ?? '',
      counterOf: t?.counterOf ?? '',
      done: t?.done ?? '',
      closeLabel: t?.closeLabel ?? '',
      videoUnavailable: t?.videoUnavailable ?? '',
    }),
    [t]
  );

  const handleDone = useCallback(
    async (watched: MustWatchVideoNotification) => {
      await markVideoNotificationWatchedApi(watched.id);
      // Flags the video here and in every other tab of this app, so their
      // blocking overlay lifts immediately instead of waiting for their own
      // recall. This replaces the old refetch, which cost a request per
      // confirmation and could land before the backend had dropped the item
      // from the queue.
      markWatched(watched.id);
    },
    [markWatched]
  );

  return {
    notifications,
    translations: mustWatchTranslations,
    handleDone,
    enabled,
  };
};

export default useMustWatchVideoNotification;
